// Table Models
export interface TableConfig {
  header: string;
  field: string;
  fieldType: FieldType;
  formatType?: FormatType;
  fuelFormatType?: FormatType;
  searchable?: boolean;
  noAdvanceFiltering?: boolean;
  hidden?: boolean;
  outOfView?: boolean;
  style?: string;
  identifier?: boolean;
  dateFilter?: boolean;
  strikethroughZeros?: boolean;
  objectDescription?: boolean;
}

export enum FieldType {
  STRING = "string",
  NUMBER = "number",
  DATE = "date",
  DATE_TIME = "datetime",
  BOOLEAN = "boolean",
  OPTIONS = "options",
  PROGRESS = "progress",
  CHIP_STRING = "chip_string",
  CHIP_NUMBER = "chip_number",
  ICON = "icon",
  COLOR = "color",
  ARRAY = "array",
  IMAGE = "image",
  OBJECT = "object",
  OBJECT_DESCRIPTION = "object_description",
  COUNTRY = "country",
  DOWNLOAD = "download",
}

export enum PropertyDataTypeCode {
  TEXT = "TEXT",
  NUMERIC = "NUMERIC",
  DATE = "DATE",
  BOOLEAN = "BOOLEAN",
}

export enum FormatType {
  DATE = "dateFormat",
  TIME = "timeFormat",
  DATETIME = "datetimeFormat",
  AMOUNT = "amountFormat",
  UNITPRICE = "unitPriceFormat",
  FUELUNITPRICE = "fuelUnitPriceFormat",
  UOM = "uomFormat",
  FUELUOM = "fuelUomFormat",
  PERCENT = "percentageFormat",
}

export enum CategoriesPropertiesPrefixes {
  CATEGORIES = "categories.",
  PROPERTIES = "properties.",
}

export enum DefaultPage {
  PAGE_INDEX = 0,
  PAGE_SIZE = 50,
  EXCEL_PAGE_SIZE = 100000,
}

export interface TableEvent extends ExportEvent {
  pageEvent: PageEvent;
}

export interface ExportEvent {
  pageEvent: PageEvent;
  sortEvent: SortEvent;
  searchEvent: SearchEvent;
  filterEvent: FilterEvent;
}

export interface PageEvent {
  pageIndex: number;
  pageSize: number;
}

export interface SortEvent {
  active: string;
  direction: string;
}

export interface SearchEvent {
  filterConfig: TableConfig[];
  searchTerms: string[];
}

export interface FilterEvent {
  advancedFilter: AdvancedFilter[];
}

export interface ColumnHidden {
  header: string;
  hidden: boolean;
}

export interface ColumnSorterInfo extends ColumnFilterInfo {
  hidden: boolean;
  pin: boolean;
  filtered?: boolean;
}

export interface ColumnFilterInfo {
  id: string;
  name: string;
}

export enum ConfigSettingType {
  TABLE_CONFIG = "TABLE_CONFIG",
  OTHER = "OTHER",
}

export enum ConfigScope {
  USER = "USER",
}

export enum ExportFormat {
  XLSX = "xlsx",
  CSV = "csv",
}

export enum ExportLimit {
  CURRENT = "CURRENT",
  ALL = "ALL",
}

// Advanced Filter Models
export interface AdvancedFilter {
  id?: string;
  column: string;
  fieldType?: FieldType;
  operator: FilterName;
  value: string | number | boolean;
}

export enum FilterName {
  CONTAINS = "FilterOperations.contains",
  STARTS_WITH = "FilterOperations.startsWith",
  ENDS_WITH = "FilterOperations.endsWith",
  EQUALS_TO = "FilterOperations.equalsTo",
  EQUALS = "FilterOperations.equals",
  EQUAL = "FilterOperations.equal",
  NOT_EQUAL = "FilterOperations.notEqual",
  DOES_NOT_EQUAL = "FilterOperations.doesNotEqual",
  DOES_NOT_CONTAIN = "FilterOperations.doesNotContain",
  GREATER_THAN = "FilterOperations.greaterThan",
  LESS_THAN = "FilterOperations.lessThan",
  GREATER_THAN_EQUAL = "FilterOperations.greaterThanEqual",
  LESS_THAN_EQUAL = "FilterOperations.lessThanEqual",
  IN = "FilterOperations.in",
  BEFORE = "FilterOperations.before",
  AFTER = "FilterOperations.after",
  AND = "FilterOperations.and",
  OR = "FilterOperations.or",
}

export enum FilterOperation {
  CONTAINS = "=ilike=",
  EQUALS = "==",
  NOT_EQUAL = "!=",
  DOES_NOT_EQUAL = "=out=",
  GREATER_THAN = "=gt=",
  LESS_THAN = "=lt=",
  GREATER_THAN_EQUAL = "=ge=",
  LESS_THAN_EQUAL = " =le=",
  IN = "=in=",
  BEFORE = "<",
  AFTER = ">",
  AND = "and",
  OR = "or",
}

export const STRING_OPERATORS: FilterName[] = [
  FilterName.CONTAINS,
  FilterName.EQUALS_TO,
  FilterName.STARTS_WITH,
  FilterName.ENDS_WITH,
];

export const NUMBER_OPERATORS: FilterName[] = [
  FilterName.EQUALS,
  FilterName.NOT_EQUAL,
  FilterName.GREATER_THAN,
  FilterName.LESS_THAN,
  FilterName.GREATER_THAN_EQUAL,
  FilterName.LESS_THAN_EQUAL,
];

export const DATE_OPERATORS: FilterName[] = [
  FilterName.GREATER_THAN,
  FilterName.LESS_THAN,
];

export const BOOLEAN_OPERATORS: FilterName[] = [FilterName.EQUAL];
